import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CheckCircle, AlertTriangle, Loader, X, Square, Timer } from 'lucide-react';

const TODAS_AS_LOJAS = ['ANV','BVV','FER','GTV','PCI','PCS','MAL','MOX','RDP','SED','SVV','VEL','VSH','LIN'];

const fmtData  = (d) => d ? new Date(d).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-';
const fmtSecs  = (s) => {
    if (!s && s !== 0) return '-';
    if (s < 60)   return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
    return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

// ---------------------------------------------------------------------------
// CHIP DE LOJA — sigla + registros
// ---------------------------------------------------------------------------
const LojaChip = ({ item }) => {
    const ok = item.status === 'concluido';
    return (
        <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '4px 10px', borderRadius: '20px', flexShrink: 0,
            backgroundColor: ok ? '#ecfdf5' : '#fffbeb',
            border: `1px solid ${ok ? '#6ee7b7' : '#fde68a'}`,
            fontSize: '12px', fontWeight: '700',
            color: ok ? '#059669' : '#d97706',
        }}>
            {ok ? <CheckCircle size={11} /> : <AlertTriangle size={11} />}
            {item.loja}
            <span style={{ fontWeight: '500', fontSize: '11px', color: ok ? '#6ee7b7' : '#fcd34d' }}>
                · {Number(item.registros).toLocaleString('pt-BR')}
            </span>
        </div>
    );
};

// ---------------------------------------------------------------------------
// PAINEL DE UM JOB ESPECÍFICO
// ---------------------------------------------------------------------------
const MonitorPanel = ({ job, onDismiss }) => {
    const [progresso,     setProgresso]     = useState([]);
    const [logInfo,       setLogInfo]       = useState(null);
    const [finalizado,    setFinalizado]    = useState(false);
    const [lojaAtual,     setLojaAtual]     = useState(null);
    const [elapsed,       setElapsed]       = useState(0);
    const [interrompendo, setInterrompendo] = useState(false);
    const startRef = useRef(null);

    const token   = localStorage.getItem('authToken');
    const headers = { 'Authorization': `Bearer ${token}` };

    const fetchProgresso = useCallback(async () => {
        try {
            const res = await fetch(`/api/monitoramento/progresso/${job.id}`, { headers });
            if (!res.ok) return;
            const data = await res.json();
            setProgresso(data.progresso || []);

            if (data.log) {
                setLogInfo(data.log);
                if (!startRef.current && data.log.data_inicio) {
                    startRef.current = new Date(data.log.data_inicio);
                }
            }

            const concluidas = new Set((data.progresso || []).map(p => p.loja));
            const detLog = data.log?.detalhes
                ? (typeof data.log.detalhes === 'string' ? JSON.parse(data.log.detalhes) : data.log.detalhes)
                : null;
            const ordemReal = detLog?.lojas_previstas || detLog?.lojas_processadas || TODAS_AS_LOJAS;
            setLojaAtual(ordemReal.find(l => !concluidas.has(l)) || null);

            if (data.log?.data_fim) setFinalizado(true);
        } catch (e) { /* silencioso */ }
    }, [job.id]); // eslint-disable-line

    // Polling
    useEffect(() => {
        fetchProgresso();
        if (finalizado) return;
        const interval = setInterval(fetchProgresso, 3000);
        return () => clearInterval(interval);
    }, [fetchProgresso, finalizado]);

    // Timer em tempo real — usa useRef para evitar closure bug
    const timerRef = useRef(null);
    useEffect(() => {
        if (timerRef.current) clearInterval(timerRef.current);

        // Base: logInfo (mais preciso) ou job.data_inicio (disponível imediatamente)
        const raw = logInfo?.data_inicio || job.data_inicio;
        if (!raw) return;
        const base = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T'));

        const update = () => {
            if (finalizado && logInfo?.data_fim) {
                const fim = new Date(logInfo.data_fim.includes('T') ? logInfo.data_fim : logInfo.data_fim.replace(' ', 'T'));
                setElapsed(Math.floor((fim - base) / 1000));
            } else {
                setElapsed(Math.floor((Date.now() - base.getTime()) / 1000));
            }
        };

        update(); // tick imediato — garante que aparece sem esperar 1s
        timerRef.current = setInterval(update, 1000);
        return () => clearInterval(timerRef.current);
    }, [finalizado, logInfo?.data_fim, logInfo?.data_inicio, job.data_inicio]); // eslint-disable-line

    const handleInterromper = async () => {
        if (!window.confirm('Confirma a interrupção? Os dados já inseridos são preservados.')) return;
        setInterrompendo(true);
        try {
            await fetch(`/api/monitoramento/interromper/${job.id}`, { method: 'POST', headers });
            setFinalizado(true);
        } catch (e) { /* silencioso */ } finally {
            setInterrompendo(false);
        }
    };

    // Usa lojas_previstas do log (gravado no INSERT inicial) para o total correto
    const detalhesLog    = logInfo?.detalhes
        ? (typeof logInfo.detalhes === 'string' ? JSON.parse(logInfo.detalhes) : logInfo.detalhes)
        : null;
    const lojasPrevistas = detalhesLog?.lojas_previstas || detalhesLog?.lojas_processadas || detalhesLog?.lojas;
    const total      = lojasPrevistas ? lojasPrevistas.length : TODAS_AS_LOJAS.length;
    const concluidas = progresso.length;
    const pct        = Math.round((concluidas / total) * 100);
    const hash       = logInfo?.hash_execucao || job.hash_execucao;
    const jobName    = logInfo?.job_name      || job.job_name || 'ETL';
    const temErro    = progresso.some(p => p.status === 'erro') || (finalizado && job.status === 'erro');
    const corBarra   = finalizado ? (temErro ? '#f59e0b' : '#10b981') : '#3b82f6';
    const periodoInicio = logInfo?.periodo_inicio || job.periodo_inicio;
    const periodoFim    = logInfo?.periodo_fim    || job.periodo_fim;

    return (
        <div style={{
            backgroundColor: '#fff',
            border: '1px solid #e2e8f0',
            borderLeft: `4px solid ${corBarra}`,
            borderRadius: '12px',
            padding: '16px 20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
        }}>
            {/* CABEÇALHO */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                    {finalizado
                        ? (temErro ? <AlertTriangle size={15} color="#f59e0b" /> : <CheckCircle size={15} color="#10b981" />)
                        : <Loader size={15} color="#3b82f6" className="animate-spin" />
                    }
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {/* Nome do processo */}
                        <span style={{ fontSize: '11px', fontWeight: '800', padding: '2px 8px', borderRadius: '10px', backgroundColor: '#eff6ff', color: '#3b82f6' }}>
                            {jobName}
                        </span>
                        <span style={{ fontWeight: '800', fontSize: '13px', color: '#0f172a' }}>
                            {finalizado ? (temErro ? 'Interrompido / Erro' : 'Concluído') : 'Executando...'}
                        </span>
                        {hash && (
                            <span style={{ fontFamily: 'monospace', fontSize: '11px', color: '#94a3b8' }}>#{hash}</span>
                        )}
                    </div>
                </div>

                {/* TIMER + BOTÕES */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '6px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', fontSize: '12px', fontWeight: '700', color: elapsed > 600 ? '#f59e0b' : '#64748b' }}>
                        <Timer size={11} />
                        {fmtSecs(elapsed)}
                    </div>

                    {!finalizado && (
                        <button
                            onClick={handleInterromper}
                            disabled={interrompendo}
                            title="Interromper execução"
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', borderRadius: '6px', border: '1px solid #fecdd3', backgroundColor: '#fff1f2', color: '#e11d48', fontSize: '11px', fontWeight: '700', cursor: interrompendo ? 'not-allowed' : 'pointer', opacity: interrompendo ? 0.6 : 1 }}
                        >
                            <Square size={9} />
                            {interrompendo ? 'Parando...' : 'Interromper'}
                        </button>
                    )}

                    <button onClick={onDismiss} title="Fechar painel" style={{ padding: '4px 5px', borderRadius: '6px', border: '1px solid #e2e8f0', backgroundColor: 'transparent', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        <X size={12} />
                    </button>
                </div>
            </div>

            {/* PERÍODO */}
            {periodoInicio && (
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px', fontWeight: '600' }}>
                    Período:&nbsp;
                    <span style={{ color: '#334155' }}>
                        {fmtData(periodoInicio)}
                        {periodoInicio !== periodoFim && periodoFim ? ` → ${fmtData(periodoFim)}` : ''}
                    </span>
                </div>
            )}

            {/* BARRA */}
            <div style={{ height: '5px', backgroundColor: '#f1f5f9', borderRadius: '99px', marginBottom: '10px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, borderRadius: '99px', backgroundColor: corBarra, transition: 'width 0.5s ease' }} />
            </div>

            {/* CHIPS */}
            {progresso.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: lojaAtual && !finalizado ? '8px' : '0' }}>
                    {progresso.map(item => <LojaChip key={item.loja} item={item} />)}
                </div>
            )}

            {/* LOJA ATUAL */}
            {lojaAtual && !finalizado && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 10px', backgroundColor: '#eff6ff', borderRadius: '8px', fontSize: '12px', color: '#3b82f6', fontWeight: '600' }}>
                    <Loader size={11} className="animate-spin" />
                    <strong>{lojaAtual}</strong> processando...
                    <span style={{ marginLeft: 'auto', color: '#94a3b8', fontWeight: '500', fontSize: '11px' }}>
                        {concluidas}/{total}
                    </span>
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// WRAPPER — renderiza um painel por job (rodando ou recém-finalizado)
// ---------------------------------------------------------------------------
const LiveMonitor = ({ logs }) => {
    // Persiste IDs fechados na sessão — não reaparece no refresh
    const [dismissed, setDismissed] = useState(() => {
        try {
            const saved = sessionStorage.getItem('lm_dismissed');
            return saved ? new Set(JSON.parse(saved)) : new Set();
        } catch { return new Set(); }
    });

    const dismiss = (id) => {
        setDismissed(prev => {
            const next = new Set([...prev, id]);
            try { sessionStorage.setItem('lm_dismissed', JSON.stringify([...next])); } catch {}
            return next;
        });
    };

    const jobsRodando = (logs || []).filter(l => l.status === 'rodando');

    // Jobs recém-finalizados (últimos 5 min) que não foram dispensados
    const agora = Date.now();
    const jobsRecentes = (logs || []).filter(l =>
        ['sucesso','parcial','erro'].includes(l.status) &&
        l.data_fim &&
        (agora - new Date(l.data_fim).getTime()) < 5 * 60 * 1000 &&
        !dismissed.has(l.id)
    );

    const jobsVisiveis = [
        ...jobsRodando,
        ...jobsRecentes.filter(j => !jobsRodando.find(r => r.id === j.id)),
    ].filter(j => !dismissed.has(j.id));

    if (jobsVisiveis.length === 0) return null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {jobsVisiveis.map(job => (
                <MonitorPanel
                    key={job.id}
                    job={job}
                    onDismiss={() => dismiss(job.id)}
                />
            ))}
        </div>
    );
};

export default LiveMonitor;
